import * as Sentry from "@sentry/react";

import { USER_REJECTION_PATTERNS } from "../errors/TransactionError";
import { isProductionHost } from "../utils/environment";

const SENTRY_DSN = "https://59521a96423031746d31d29cbc56aa3d@o4511088833265664.ingest.us.sentry.io/4511088834641920";

let initialized = false;

function isUserRejectionError(event: Sentry.ErrorEvent): boolean {
    const message = event.exception?.values?.[0]?.value?.toLowerCase() ?? "";
    return USER_REJECTION_PATTERNS.some((pattern) => message.includes(pattern));
}

interface SentryConfig {
    appName: string;
}

/**
 * Initialize Sentry error monitoring and tracing.
 * Active in all environments — environment tag differentiates production vs development.
 */
export function initSentry({ appName }: SentryConfig): void {
    if (initialized) {
        return;
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        sendDefaultPii: false,
        environment: isProductionHost() ? "production" : "development",
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 1.0,
        // Only propagate traces to same-origin relative requests. Using "localhost"
        // would match cross-origin localhost:PORT requests and cause CORS errors
        // (sentry-trace header not in allowed headers list).
        tracePropagationTargets: [/^\/api\//],
        initialScope: {
            tags: {
                app: appName
            }
        },
        beforeSend(event) {
            if (isUserRejectionError(event)) {
                return null;
            }
            // Strip auth-related breadcrumbs and request bodies to avoid
            // capturing credentials or tokens in error reports.
            if (event.request) {
                delete event.request.data;
                delete event.request.cookies;
            }
            if (event.breadcrumbs?.values) {
                const crumbs = event.breadcrumbs.values as unknown as Sentry.Breadcrumb[];
                event.breadcrumbs.values = crumbs.filter(
                    (b: Sentry.Breadcrumb) => b.category !== "xhr" && b.category !== "fetch"
                ) as never;
            }
            return event;
        }
    });

    initialized = true;
}

/**
 * Capture an error in Sentry with optional context. No-op if Sentry is not initialized.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
    if (!initialized) {
        return;
    }
    Sentry.captureException(error, { extra: context });
}
